import dayjs from "@punchup/utils/dayjs";

import type {
  AutomationsBillingInterval,
  AutomationsTier,
} from "./automations-plans";
import type { AutomationsPlanTier } from "./automations-plan-metadata";

export const AUTOMATIONS_ENTITLEMENT_STATUSES = [
  "free",
  "trial",
  "active",
  "lapsed",
  "canceled",
] as const;
export type AutomationsEntitlementStatus =
  (typeof AUTOMATIONS_ENTITLEMENT_STATUSES)[number];

/** What the Plan & usage card switches on, derived from the live Stripe status. */
export type AutomationsState = "subscribed" | "not_purchased" | "cancelled";

/** Stripe subscription metadata `purchase_category` for Automations. */
export const AUTOMATIONS_PURCHASE_CATEGORY = "automations";

/**
 * Maps a Stripe subscription status (or none) onto the entitlement union.
 * An abandoned checkout (`incomplete*`), no subscription and unknown values
 * read `free`.
 */
export function entitlementStatusFromStripe(
  stripeStatus: string | null | undefined
): AutomationsEntitlementStatus {
  switch (stripeStatus) {
    case "active":
      return "active";
    case "trialing":
      return "trial";
    case "past_due":
    case "unpaid":
      return "lapsed";
    case "canceled":
      return "canceled";
    default:
      return "free";
  }
}

export function isEntitledStatus(status: AutomationsEntitlementStatus) {
  return status === "active" || status === "trial";
}

/** Active, trial or past due: the automationsState Subscribed. */
export function isSubscribedStatus(status: AutomationsEntitlementStatus) {
  return automationsStateFrom(status) === "subscribed";
}

/** Past due is still Subscribed (a billingState, not a state of its own). */
export function automationsStateFrom(
  status: AutomationsEntitlementStatus
): AutomationsState {
  switch (status) {
    case "trial":
    case "active":
    case "lapsed":
      return "subscribed";
    case "canceled":
      return "cancelled";
    case "free":
      return "not_purchased";
  }
}

export interface AutomationsEntitlementSnapshot {
  /** Null for a tier this build does not know. */
  tier: AutomationsPlanTier | null;
  /** Monthly credit pool for every interval (annual plans keep a monthly pool). */
  creditAllotment: number | null;
  /** From the base price's recurring interval; null when it has none. */
  interval: AutomationsBillingInterval | null;
  /** ISO timestamp of the current billing period's start. */
  currentPeriodStart: string | null;
  /** ISO timestamp of the current billing period's end. */
  currentPeriodEnd: string | null;
  /** ISO end of service when a cancellation is scheduled (`cancel_at`, or the period end under `cancel_at_period_end`); the end date once canceled. */
  cancelAt: string | null;
  /** Checkout's `overage_enabled` metadata; null when the subscription predates it. */
  overageEnabled: boolean | null;
  /** Checkout's `overage_cap_cents` metadata; null when absent or out of range. */
  overageCapCents: number | null;
  /** The latest invoice's charge and the default card; null when the subscription isn't expanded. */
  lastPayment?: AutomationsLastPayment | null;
  /** A change waiting for the renewal (a scheduled downgrade or cycle change); null when none, or the subscription isn't expanded. */
  pendingChange?: AutomationsPendingChange | null;
}

export interface AutomationsPendingChange {
  tier: AutomationsTier;
  interval: AutomationsBillingInterval;
  /** ISO start of the new plan, the renewal. */
  effectiveAt: string;
}

export interface AutomationsLastPayment {
  amountCents: number;
  /** ISO creation date of the latest invoice: the failed one while past due. */
  createdAt: string | null;
  /** Null for a wallet / Link method with no card. */
  cardLast4: string | null;
}

/** The in-place plan changes the subscription route accepts. */
export type AutomationsSubscriptionAction =
  | "preview_change"
  | "change"
  | "cancel"
  | "resume";

/** The read-only quote for a change that takes effect now. */
export interface AutomationsUpgradePreview {
  /** What the card is charged today, prorated. */
  amountDueCents: number;
}

/** The banner-worthy condition of a Subscribed plan, most urgent first. */
export type AutomationsBillingState =
  | "past_due"
  | "at_cap_paused"
  | "near_cap"
  | "over_cap"
  | "cancelling"
  | "active";

/** Overages off and this share of the allotment used warns before the pause. */
export const NEAR_CAP_USED_RATIO = 0.8;

export interface AutomationsPoolUsage {
  balance: number;
  allotment: number;
}

/**
 * The cap states read the live balance, since zero is what pauses sends.
 * The pool resets to the allotment each month, so spent = allotment − balance.
 */
export function automationsCapState(
  snapshot: AutomationsEntitlementSnapshot | null,
  usage: AutomationsPoolUsage | null
): "at_cap_paused" | "over_cap" | "near_cap" | null {
  if (!usage || usage.allotment <= 0) return null;
  const overageOn = snapshot?.overageEnabled !== false;
  if (usage.balance <= 0) return overageOn ? "over_cap" : "at_cap_paused";
  const spent = usage.allotment - usage.balance;
  return !overageOn && spent >= usage.allotment * NEAR_CAP_USED_RATIO
    ? "near_cap"
    : null;
}

/** Null usage (not loaded) never yields a cap state. */
export function automationsBillingStateFrom({
  status,
  snapshot,
  usage,
  now,
}: {
  status: AutomationsEntitlementStatus;
  snapshot: AutomationsEntitlementSnapshot | null;
  usage: AutomationsPoolUsage | null;
  now: string | Date;
}): AutomationsBillingState {
  if (status === "lapsed") return "past_due";
  const cap = automationsCapState(snapshot, usage);
  if (cap) return cap;
  if (snapshot?.cancelAt && dayjs.utc(now).isBefore(snapshot.cancelAt)) {
    return "cancelling";
  }
  return "active";
}

export interface AutomationsEntitlementResponse {
  status: AutomationsEntitlementStatus;
  snapshot: AutomationsEntitlementSnapshot | null;
}
